import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import { buildTiktokPath } from '@/tiktok-platform/constants/tiktokRoutes';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import HoverCopyField from '@/tiktok-platform/modules/products/components/HoverCopyField';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { buildTiktokProductTree } from '@/tiktok-platform/utils/tiktokProductTree';
import { tiktokActivityTypeLabel } from '@/tiktok-platform/utils/tiktokMarketingDisplay';
import { formatPriceRangeDisplay } from '@/utils/formatPriceRange';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-platform.css';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

function enrollUsesPrice(activityType) {
  const type = String(activityType || '').toUpperCase();
  return type === 'FIXED_PRICE' || type === 'FLASHSALE';
}

function percentOffLabel(rate) {
  const off = Number(rate);
  if (!Number.isFinite(off) || off <= 0) return '';
  const remain = 100 - off;
  if (remain > 0 && remain % 10 === 0) {
    return fixedUiTpl('减 {0}%（打{1}折）', off, remain / 10);
  }
  return fixedUiTpl('减 {0}%', off);
}

/** 运费折扣、满减、买赠的优惠在创建活动时已经写死，报名接口不收活动价。 */
function fixedActivityOffer(activity, activityType) {
  const discount = activity?.discount && typeof activity.discount === 'object' ? activity.discount : null;
  const type = String(activityType || '').toUpperCase();
  const shipping = discount?.shipping_discount || discount?.shippingDiscount;
  if (type === 'SHIPPING_DISCOUNT' || shipping) {
    const shipType = String(shipping?.type || 'FREE_SHIPPING').toUpperCase();
    const value = shipping?.value ?? shipping?.discount_value ?? shipping?.discountValue;
    if (shipType === 'FREE_SHIPPING' || value == null || value === '' || Number(value) === 0) {
      return { applyToPrice: false, discountText: fixedUi('运费全免') };
    }
    const shipOff = percentOffLabel(value) || fixedUiTpl('减 {0}', value);
    return { applyToPrice: false, discountText: fixedUiTpl('运费{0}', shipOff) };
  }
  const bmsm = discount?.bmsm_discount || discount?.bmsmDiscount;
  const tier = Array.isArray(bmsm?.details) ? bmsm.details[0] : null;
  if (tier) {
    const rate = Number(tier.value);
    const tierType = String(tier.type || '').toUpperCase();
    if (tierType.includes('PERCENT') && Number.isFinite(rate) && rate > 0) {
      return { applyToPrice: true, mode: 'percent', rate, discountText: percentOffLabel(rate) };
    }
    if (tier.value != null && tier.value !== '') {
      return { applyToPrice: true, mode: 'amount', amount: Number(tier.value), discountText: fixedUiTpl('立减 {0}', tier.value) };
    }
  }
  const bxgy = discount?.bxgy_discount || discount?.bxgyDiscount;
  if (type === 'BUY_X_GET_Y' || bxgy) {
    const rate = Number(bxgy?.discount_value ?? bxgy?.discountValue);
    if (Number.isFinite(rate) && rate > 0) {
      return { applyToPrice: false, discountText: fixedUiTpl('赠品{0}', percentOffLabel(rate)) };
    }
    return { applyToPrice: false, discountText: fixedUi('买赠，商品价不变') };
  }
  if (type === 'BUY_MORE_SAVE_MORE') {
    return { applyToPrice: false, discountText: fixedUi('折扣已固定') };
  }
  return null;
}

function priceAfterDiscount(raw, currency, offer) {
  const amount = Number(raw);
  if (!Number.isFinite(amount)) return '—';
  const prefix = currency ? `${currency} ` : '';
  if (!offer?.applyToPrice) return `${prefix}${amount}`;
  let next = amount;
  if (offer.mode === 'percent' && offer.rate > 0 && offer.rate < 100) {
    next = Math.round(amount * (100 - offer.rate)) / 100;
  } else if (offer.mode === 'amount' && Number.isFinite(offer.amount)) {
    next = Math.max(0, Math.round((amount - offer.amount) * 100) / 100);
  }
  return `${prefix}${next.toFixed(2)}`;
}

function skuRefs(product) {
  const skus = product?.payload?.skus;
  if (!Array.isArray(skus)) return [];
  return skus
    .map((sku) => ({
      id: String(sku?.id || sku?.sku_id || '').trim(),
      currency: sku?.price?.currency || product?.currency || '',
    }))
    .filter((sku) => sku.id);
}

function skuSizeLabel(sku) {
  return sku?.sizeName || sku?.sizeLabel || sku?.skuSpecLabel || sku?.specName || sku?.skuSize || '—';
}

function formatSkcPriceRange(skus, fallbackCurrency = 'USD') {
  const list = Array.isArray(skus) ? skus : [];
  if (!list.length) return '—';
  return formatPriceRangeDisplay(
    list.map((sku) => ({
      value: sku?.salePrice ?? sku?.supplyPrice,
      currency: sku?.currency,
    })),
    fallbackCurrency,
  );
}

function productIdOf(row) {
  const id = String(row?.productId || row?.id || row?.spuId || '').trim();
  return id === '—' ? '' : id;
}

function quoteKey(productId, skuId, variationLevel) {
  return variationLevel ? `${productId}::${skuId}` : String(productId || '');
}

function offerInvalid(text, asDiscount) {
  const offer = String(text || '').trim();
  if (!asDiscount) return !(Number(offer) > 0);
  const value = Number(offer);
  return !(value > 0) || !(value < 100);
}

function parseLimit(text) {
  const limitText = String(text || '').trim();
  if (limitText === '') return -1;
  const limit = Number(limitText);
  if (limit === -1) return -1;
  if (!Number.isInteger(limit) || limit < 1 || limit > 99) return null;
  return limit;
}

function matchesKeyword(row, keyword) {
  if (!keyword) return true;
  const text = [row.productId, row.id, row.title, row.productName, row.supplierCode, row.sellerSku]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return text.includes(keyword);
}

function listItems(res) {
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.content)) return res.content;
  return [];
}

/**
 * 活动报名页：在新页面选择在售商品并提交报名。
 */
export default function TiktokMarketingEnrollPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { thematicId: activityId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const shopId = String(searchParams.get('shopId') || '').trim();
  const activityName = String(searchParams.get('name') || '').trim();
  const activityType = String(searchParams.get('activityType') || '').trim();
  const productLevelQuery = String(searchParams.get('productLevel') || '').trim().toUpperCase();

  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [quotes, setQuotes] = useState({});
  const [batchOffer, setBatchOffer] = useState('');
  const [batchLimit, setBatchLimit] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedRows, setSelectedRows] = useState(() => new Map());
  const [submitting, setSubmitting] = useState(false);
  const [activityMeta, setActivityMeta] = useState(null);

  const resolvedType = String(activityMeta?.activityType || activityType || '').toUpperCase();
  const resolvedLevel = String(
    activityMeta?.productLevel || activityMeta?.product_level || productLevelQuery || '',
  ).toUpperCase();
  const levelReady = activityMeta != null || Boolean(productLevelQuery);
  const variationLevel = resolvedLevel === 'VARIATION';
  const priceMode = enrollUsesPrice(resolvedType);
  const offerIsDiscount = resolvedType === 'DIRECT_DISCOUNT';
  const needsOffer = priceMode || offerIsDiscount || variationLevel;
  const fixedOffer = needsOffer ? null : fixedActivityOffer(activityMeta, resolvedType);
  const typeLabel = resolvedType ? tiktokActivityTypeLabel(resolvedType) : fixedUi('营销活动');

  useEffect(() => {
    if (!shopId || !activityId) return undefined;
    let cancelled = false;
    tiktokPlatformAPI.getMarketingActivityDetail(shopId, activityId)
      .then((res) => {
        if (!cancelled) setActivityMeta(res?.activity || res?.thematicInfo || {});
      })
      .catch(() => {
        if (!cancelled) setActivityMeta({});
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, activityId]);

  const load = useCallback(async () => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const needle = keyword.trim().toLowerCase();
      if (!needle) {
        const res = await tiktokPlatformAPI.listProducts({
          shopId,
          page,
          pageSize,
          listingDimension: 'listed',
        });
        setRows(listItems(res));
        setTotal(Number(res?.total ?? res?.totalElements) || 0);
        return;
      }
      const matched = [];
      let seen = 0;
      let reportedTotal = 0;
      for (let pageNo = 1; pageNo <= 8; pageNo += 1) {
        const res = await tiktokPlatformAPI.listProducts({
          shopId,
          page: pageNo,
          pageSize: 50,
          listingDimension: 'listed',
        });
        const items = listItems(res);
        reportedTotal = Number(res?.total ?? res?.totalElements) || reportedTotal;
        seen += items.length;
        matched.push(...items.filter((row) => matchesKeyword(row, needle)));
        if (!items.length || seen >= reportedTotal) break;
      }
      const start = (page - 1) * pageSize;
      setRows(matched.slice(start, start + pageSize));
      setTotal(matched.length);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || fixedUi('加载店铺商品失败'));
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, keyword]);

  useEffect(() => {
    load();
  }, [load]);

  const tree = useMemo(() => buildTiktokProductTree(rows), [rows]);

  const allChecked = useMemo(() => {
    const ids = tree.map((item) => productIdOf(item)).filter(Boolean);
    return ids.length > 0 && ids.every((id) => selectedIds.has(id));
  }, [tree, selectedIds]);

  const toggleOne = (product) => {
    const id = productIdOf(product);
    if (!id) return;
    const source = product._seed || product;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectedRows((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, source);
      return next;
    });
  };

  const togglePage = () => {
    const ids = tree.map((item) => productIdOf(item)).filter(Boolean);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
    setSelectedRows((prev) => {
      const next = new Map(prev);
      if (allChecked) ids.forEach((id) => next.delete(id));
      else tree.forEach((product) => {
        const id = productIdOf(product);
        if (id) next.set(id, product._seed || product);
      });
      return next;
    });
  };

  const submit = async () => {
    if (!shopId || !activityId || submitting) return;
    const chosen = [...selectedIds].map((id) => selectedRows.get(id)).filter(Boolean);
    if (!chosen.length) {
      showToast(fixedUi("请先选择商品"), 'warning');
      return;
    }
    if (!levelReady) {
      showToast(fixedUi("正在读取活动的报价维度"), 'warning');
      return;
    }
    const readOffer = (key, currency) => {
      const offer = String(quotes[key]?.offer || '').trim();
      if (offerInvalid(offer, offerIsDiscount)) {
        showToast(offerIsDiscount ? fixedUi('请填写 1 到 99 的折扣百分比') : fixedUi('请填写活动价'), 'warning');
        return null;
      }
      return priceMode || !offerIsDiscount
        ? { activityPrice: { amount: offer, currency } }
        : { discount: offer };
    };
    const readLimits = (key) => {
      const limit = parseLimit(quotes[key]?.limit);
      if (limit == null) {
        showToast(fixedUi("限购数量须为 1 到 99，留空表示不限"), 'warning');
        return null;
      }
      return { quantityLimit: limit, quantityPerUser: -1 };
    };
    const productList = [];
    for (const row of chosen) {
      const productId = productIdOf(row);
      const currency = row.currency || skuRefs(row)[0]?.currency || 'USD';
      if (variationLevel) {
        const refs = skuRefs(row);
        if (!refs.length) {
          showToast(fixedUiTpl('商品 {0} 缺少 SKU，无法按规格提报', productId), 'warning');
          return;
        }
        const skus = [];
        for (const sku of refs) {
          const key = quoteKey(productId, sku.id, true);
          const offerFields = needsOffer ? readOffer(key, sku.currency || currency) : {};
          const limits = readLimits(key);
          if ((needsOffer && !offerFields) || !limits) return;
          skus.push({ id: sku.id, ...offerFields, ...limits });
        }
        productList.push({
          productId,
          quantityLimit: -1,
          quantityPerUser: -1,
          skus,
        });
      } else {
        const key = quoteKey(productId, '', false);
        const offerFields = needsOffer ? readOffer(key, currency) : {};
        const limits = readLimits(key);
        if ((needsOffer && !offerFields) || !limits) return;
        productList.push({ productId, ...offerFields, ...limits });
      }
    }
    setSubmitting(true);
    try {
      await tiktokPlatformAPI.submitMarketingEnroll(shopId, activityId, { productList });
      showToast(fixedUi("报名已提交"), 'success');
      setSelectedIds(new Set());
      setSelectedRows(new Map());
      setQuotes({});
    } catch (e) {
      showToast(e?.message || fixedUi('报名失败'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const applyBatch = () => {
    if (!selectedIds.size) {
      showToast(fixedUi("请先选择商品"), 'warning');
      return;
    }
    if (needsOffer && offerInvalid(batchOffer, offerIsDiscount)) {
      showToast(offerIsDiscount ? fixedUi('请填写 1 到 99 的折扣百分比') : fixedUi('请填写活动价'), 'warning');
      return;
    }
    if (parseLimit(batchLimit) == null) {
      showToast(fixedUi("限购数量须为 1 到 99，留空表示不限"), 'warning');
      return;
    }
    const offer = batchOffer.trim();
    const limit = batchLimit.trim();
    setQuotes((prev) => {
      const next = { ...prev };
      selectedRows.forEach((row, productId) => {
        if (variationLevel) {
          skuRefs(row).forEach((sku) => {
            next[quoteKey(productId, sku.id, true)] = { offer, limit };
          });
        } else {
          next[quoteKey(productId, '', false)] = { offer, limit };
        }
      });
      return next;
    });
  };

  const patchQuote = (key, field, value) => {
    setQuotes((prev) => ({
      ...prev,
      [key]: { offer: '', limit: '', ...prev[key], [field]: value },
    }));
  };

  return (
    <div className="tko-page platform-product-page platform-product-page--tiktok">
      <section className="tko-card tko-card--filter">
        <div className="tko-card__section tko-card__section--filter" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
          <div>
            <h1 className="tko-page-title" style={{ margin: 0 }}>{activityName || fixedUi("活动报名")}</h1>
            <p className="tko-board-note">
              {typeLabel}
              {activityId ? fixedUiTpl(' · 活动 ID {0}', activityId) : ''}
              {variationLevel ? fixedUi(' · 按 SKU 报价') : resolvedLevel === 'PRODUCT' ? fixedUi(' · 按商品报价') : fixedUi(' · 正在读取报价维度')}
              {fixedOffer ? fixedUi(' · 折扣在创建活动时已固定，报名不再填写活动价') : ''}
              {needsOffer && variationLevel ? (offerIsDiscount ? fixedUi(' · 折扣写在每个 SKU 上，10 表示打九折') : fixedUi(' · 活动价写在每个 SKU 上')) : ''}
              {needsOffer && !variationLevel && resolvedLevel === 'PRODUCT' ? (offerIsDiscount ? fixedUi(' · 折扣写在商品上，10 表示打九折') : fixedUi(' · 活动价写在商品上，SKU 列表必须为空')) : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="tko-btn tko-btn--secondary" onClick={() => navigate(buildTiktokPath('marketing'))}>
              {fixedUi('返回活动列表')}
            </button>
            <button type="button" className="tko-btn tko-btn--primary" disabled={!shopId || submitting} onClick={submit}>
              {submitting ? fixedUi("提交中…") : fixedUiTpl('提交报名（已选 {0}）', selectedIds.size)}
            </button>
          </div>
        </div>
      </section>

      <ProductFilterCard>
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <label className="tko-field tko-field--inline">
              <span className="tko-field__label">{fixedUi("商品")}</span>
              <input
                className="tko-input"
                value={keywordDraft}
                placeholder={fixedUi("产品 ID / 名称 / 货号")}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1);
                    setKeyword(keywordDraft.trim());
                  }
                }}
              />
            </label>
          </div>
          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!shopId || loading}
              onClick={() => {
                setPage(1);
                setKeyword(keywordDraft.trim());
              }}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
        {!shopId ? <p className="tko-board-note">{fixedUi("请从活动列表进入，以便带上店铺。")}</p> : null}
        {error ? <p className="tko-board-error">{error}</p> : null}
      </ProductFilterCard>

      <ProductListCard>
        <div className="tko-enroll-batch">
          {needsOffer ? (
            <>
              <span className="tko-field__label">{offerIsDiscount ? fixedUi("批量折扣（%）") : fixedUi("批量活动价")}</span>
              <input
                className="tko-enroll-quote"
                value={batchOffer}
                inputMode="decimal"
                placeholder={offerIsDiscount ? '1-99' : fixedUi("活动价")}
                onChange={(e) => setBatchOffer(e.target.value)}
              />
            </>
          ) : null}
          <span className="tko-field__label">{fixedUi("批量限购")}</span>
          <input
            className="tko-enroll-quote"
            value={batchLimit}
            inputMode="numeric"
            placeholder={fixedUi("不限")}
            onChange={(e) => setBatchLimit(e.target.value)}
          />
          <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={applyBatch}>
            {fixedUi('填入已选')}
          </button>
        </div>
        <div className="wf-tasks-table-wrap">
          <table className="wf-tasks-table platform-product-table platform-product-table--tiktok tko-enroll-table">
            <colgroup>
              <col className="tko-enroll-col--check" />
              <col className="tko-enroll-col--info" />
              <col className="tko-enroll-col--spec" />
              <col className="tko-enroll-col--sku" />
              <col className="tko-enroll-col--price" />
              {needsOffer ? <col className="tko-enroll-col--quote" /> : null}
              {fixedOffer ? <col className="tko-enroll-col--quote" /> : null}
              {fixedOffer ? <col className="tko-enroll-col--quote" /> : null}
              <col className="tko-enroll-col--limit" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="shein-product-cell--spu-check tko-enroll-th--check">
                  <input type="checkbox" checked={allChecked} onChange={togglePage} aria-label={t('productTable.selectPage')} />
                </th>
                <th scope="col" className="tko-enroll-th--info">{t('productTable.productInfo')}</th>
                <th scope="col">{t('productTable.mainSpec')}</th>
                <th scope="col">{t('productTable.sku')}</th>
                <th scope="col">{t('productTable.price')}</th>
                {needsOffer ? <th scope="col">{offerIsDiscount ? fixedUi("折扣（%）") : fixedUi("活动价")}</th> : null}
                {fixedOffer ? <th scope="col">{fixedUi("活动折扣")}</th> : null}
                {fixedOffer ? <th scope="col">{fixedUi("折后价")}</th> : null}
                <th scope="col">{fixedUi("限购")}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && tree.length === 0 ? (
                <tr>
                  <td colSpan={(needsOffer ? 7 : 6) + (fixedOffer ? 2 : 0)} className="wf-tasks-table__empty">{fixedUi("没有可报名的在售商品，请先在商品管理同步")}</td>
                </tr>
              ) : tree.flatMap((product) => {
                const id = productIdOf(product);
                const skcList = product.skcs?.length
                  ? product.skcs
                  : [{ key: `${product.key}-empty`, image: product.image, specLabel: '', skus: [] }];
                const rowSpan = Math.max(1, skcList.length);
                return skcList.map((skc, skcIdx) => {
                  const skus = skc.skus || [];
                  const currency = skus[0]?.currency || product._seed?.currency || 'USD';
                  return (
                    <tr key={`${product.key}-${skc.key}-${skcIdx}`} className="shein-product-row">
                      {skcIdx === 0 ? (
                        <>
                          <td rowSpan={rowSpan} className="shein-product-cell shein-product-cell--spu-check">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(id)}
                              onChange={() => toggleOne(product)}
                              aria-label={t('productTable.selectProduct', { id })}
                            />
                          </td>
                          <td rowSpan={rowSpan} className="shein-product-cell shein-product-cell--info">
                            <div className="shein-product-info shein-product-info--text-only">
                              <div className="shein-product-info__text">
                                <HoverCopyField value={product.title} className="shein-product-info__title" popoverCopy />
                                <HoverCopyField
                                  label={t('productTable.productId')}
                                  value={id && id !== '—' ? id : ''}
                                  className="shein-product-info__meta"
                                />
                                <HoverCopyField
                                  label={t('productTable.supplier')}
                                  value={product.supplierCode || ''}
                                  className="shein-product-info__meta"
                                />
                              </div>
                            </div>
                          </td>
                        </>
                      ) : null}
                      <td className="shein-product-cell shein-product-cell--spec">
                        <div className="shein-product-spec">
                          <TiktokProductThumb imageUrl={skc.image} alt={skc.specLabel || t('productTable.mainSpec')} />
                          <div className="shein-product-spec__text">
                            {skc.specLabel ? <div className="shein-product-spec__color">{skc.specLabel}</div> : null}
                          </div>
                        </div>
                      </td>
                      <td className="shein-product-cell shein-product-cell--sku">
                        <div className="shein-product-sku-preview">
                          {skus.length ? skus.map((sku, idx) => (
                            <div key={sku.id || `${skc.key}-s-${idx}`} className="shein-product-sku-preview__item">
                              <div className="shein-product-sku-preview__size">{t('productTable.size', { size: skuSizeLabel(sku) })}</div>
                              <HoverCopyField
                                label="SKU"
                                value={sku.platformSkuId || ''}
                                className="shein-product-sku-preview__code"
                                emptyText="—"
                              />
                            </div>
                          )) : <div className="shein-product-sku-preview__item">—</div>}
                        </div>
                      </td>
                      <td className="shein-product-cell shein-product-cell--price">
                        <div className="shein-product-price">{formatSkcPriceRange(skus, currency)}</div>
                      </td>
                      {fixedOffer ? (
                        <>
                          <td className="shein-product-cell">
                            <div className="shein-product-sku-preview">
                              {(skus.length ? skus : [{}]).map((sku, idx) => (
                                <div key={sku.id || `${skc.key}-d-${idx}`} className="shein-product-sku-preview__item">
                                  {fixedOffer.discountText}
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="shein-product-cell">
                            <div className="shein-product-sku-preview">
                              {(skus.length ? skus : [{}]).map((sku, idx) => (
                                <div key={sku.id || `${skc.key}-a-${idx}`} className="shein-product-sku-preview__item">
                                  {priceAfterDiscount(sku.salePrice ?? sku.supplyPrice, sku.currency || currency, fixedOffer)}
                                </div>
                              ))}
                            </div>
                          </td>
                        </>
                      ) : null}
                      {variationLevel ? (
                        <>
                          {needsOffer ? (
                            <td className="shein-product-cell tko-enroll-cell--quote">
                              <div className="shein-product-sku-preview">
                                {skus.length ? skus.map((sku, idx) => {
                                  const skuId = sku.platformSkuId || '';
                                  const key = quoteKey(id, skuId, true);
                                  return (
                                    <div key={sku.id || `${skc.key}-q-${idx}`} className="shein-product-sku-preview__item">
                                      <input
                                        className="tko-enroll-quote"
                                        inputMode="decimal"
                                        placeholder={offerIsDiscount ? '1-99' : (sku.currency || currency)}
                                        value={quotes[key]?.offer || ''}
                                        disabled={!skuId}
                                        onChange={(e) => patchQuote(key, 'offer', e.target.value)}
                                      />
                                    </div>
                                  );
                                }) : <div className="shein-product-sku-preview__item">—</div>}
                              </div>
                            </td>
                          ) : null}
                          <td className="shein-product-cell tko-enroll-cell--quote">
                            <div className="shein-product-sku-preview">
                              {skus.length ? skus.map((sku, idx) => {
                                const skuId = sku.platformSkuId || '';
                                const key = quoteKey(id, skuId, true);
                                return (
                                  <div key={sku.id || `${skc.key}-l-${idx}`} className="shein-product-sku-preview__item">
                                    <input
                                      className="tko-enroll-quote"
                                      inputMode="numeric"
                                      placeholder={fixedUi("不限")}
                                      value={quotes[key]?.limit || ''}
                                      disabled={!skuId}
                                      onChange={(e) => patchQuote(key, 'limit', e.target.value)}
                                    />
                                  </div>
                                );
                              }) : <div className="shein-product-sku-preview__item">—</div>}
                            </div>
                          </td>
                        </>
                      ) : skcIdx === 0 ? (
                        <>
                          {needsOffer ? (
                            <td rowSpan={rowSpan} className="shein-product-cell tko-enroll-cell--quote">
                              <input
                                className="tko-enroll-quote"
                                inputMode="decimal"
                                placeholder={offerIsDiscount ? '1-99' : currency}
                                value={quotes[quoteKey(id, '', false)]?.offer || ''}
                                onChange={(e) => patchQuote(quoteKey(id, '', false), 'offer', e.target.value)}
                              />
                            </td>
                          ) : null}
                          <td rowSpan={rowSpan} className="shein-product-cell tko-enroll-cell--quote">
                            <input
                              className="tko-enroll-quote"
                              inputMode="numeric"
                              placeholder={fixedUi("不限")}
                              value={quotes[quoteKey(id, '', false)]?.limit || ''}
                              onChange={(e) => patchQuote(quoteKey(id, '', false), 'limit', e.target.value)}
                            />
                          </td>
                        </>
                      ) : null}
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel="活动报名商品"
        />
      </ProductListCard>
    </div>
  );
}
